import type { AccountPeriodRow } from "@/lib/profitability-api";

export interface MonthOverMonth {
  /** Percent change in revenue against the prior period. Null when it cannot
   *  be stated -- see `reason`. */
  revenuePct: number | null;
  /** Change in margin in PERCENTAGE POINTS, not percent-of-a-percent. Null
   *  when either month has no margin. */
  marginPoints: number | null;
  /** The month being compared against, for the cell's tooltip. */
  priorLabel: string | null;
  /** Why there is no figure, in the reader's words. Null when there is one. */
  reason: string | null;
}

/**
 * Month-over-month change for every period on the account, keyed by period id.
 *
 * Keyed rather than positional, and computed from the CHRONOLOGICAL series
 * rather than from whatever order the table happens to be displaying. The trend
 * table is sortable: sort it by revenue and "the row above" stops being "the
 * month before", and a positional delta would then silently compare July with
 * March while still calling itself month-over-month.
 *
 * "Prior period" means the previous row in the range, which is the previous
 * calendar month except where the account had a month with no activity at all
 * -- those months are absent from the series, never zero-filled. The cell says
 * which month it compared against for exactly that reason.
 *
 * @param chronological Period rows sorted oldest first.
 */
export function monthOverMonth(chronological: AccountPeriodRow[]): Map<string, MonthOverMonth> {
  const byPeriod = new Map<string, MonthOverMonth>();

  chronological.forEach((row, index) => {
    const prior = index > 0 ? chronological[index - 1] : undefined;

    if (!prior) {
      // The oldest month in the range. A dash, never 0% -- "unchanged" is a
      // claim, and there is nothing here to have changed from.
      byPeriod.set(row.period_id, {
        revenuePct: null,
        marginPoints: null,
        priorLabel: null,
        reason: "No earlier month in the selected range to compare against",
      });
      return;
    }

    const priorLabel = prior.period ?? prior.period_id;

    // A percentage change off a zero (or negative, i.e. a credit) base has no
    // value: every increase from nothing is infinite growth.
    const revenuePct =
      prior.revenue > 0 ? ((row.revenue - prior.revenue) / prior.revenue) * 100 : null;

    const marginPoints =
      row.margin_pct !== null && prior.margin_pct !== null
        ? row.margin_pct - prior.margin_pct
        : null;

    byPeriod.set(row.period_id, {
      revenuePct,
      marginPoints,
      priorLabel,
      reason:
        revenuePct === null && marginPoints === null
          ? `${priorLabel} had no revenue to compare against`
          : null,
    });
  });

  return byPeriod;
}
